import {AbstractRepository} from "../repository";
import moment from "moment";

export type LearnCategory = {
  id: string,
  parent_category_id: string|null,
  is_pinned_category: boolean,
  name: string,
  icon: string,
  created_at: string
  updated_at: string
}
export type LearnModule = {
  id: string,
  category?: LearnCategory|null,
  category_id: string|null,
  name: string,
  description: string,
  mandatory: boolean,
  certificate_type: string
  chapters: string[]
  created_at: string
  updated_at: string
  archived_at: string|null
  components?: LearnComponent[]
  assignments?: LearnAssignment[]
}
export type LearnComponent = {
  id: string,
  module_id?: string,
  name: string,
  order: number,
  chapter: string|null,
  created_at: string
  updated_at: string
  cards?: LearnCard[]
}
export type LearnCard = {
  id: string,
  type: string,
  order: number,
  title: string,
  description: string,
  media: string|null,
  properties: any,
  created_at: string
  updated_at: string
}
export type LearnAssignment = {
  id: string,
  module_id: string,
  team_id: number|null,
  employee_ons_id: number|null,
  notified_at: string|null,
  created_at: string
  updated_at: string
  card_progress?: LearnCardProgress[]
}
export type MyLearnAssignment = {
  id: string,
  module_id: string,
  learn_module?: LearnModule|null
  team_id: number|null,
  employee_ons_id: number|null,
  notified_at: string|null,
  created_at: string
  updated_at: string
  progress: LearnCardProgress[]
}
export type LearnCardProgress = {
  id: string
  learn_module_card_uuid: string,
  learn_module_assignment_uuid: string,
  answer: any,
  employee_id: number
  viewed_at: string|null
  finished_at: string|null
  created_at: string
  updated_at: string
}
export type LearnCertificate = {
  id: string
  module_id: string
  employee_ons_id: number
  pdf: string
  created_at: string
  updated_at: string
  learn_module?: LearnModule
}
export type LearnStatistics = {
  deployed_modules: {
    this_week: number,
    last_week: number,
  },
  finished_modules: {
    this_week: number,
    last_week: number,
  },
  certificates_handed_out: {
    this_week: number,
    last_week: number,
  },
}
export type LearnSearchResult = {
  item_type: string
  item_id: string
  parent_id: string
  primary_text: string
  secondary_text: string
}

export type LearnDashboardStatistic = {
  assigned_modules: {
    this_period: number,
    previous_period: number,
  },
  started_modules: {
    this_period: number,
    previous_period: number,
  },
  finished_modules: {
    this_period: number,
    previous_period: number,
  },
  dropped_modules: {
    this_period: number,
    previous_period: number,
  },
  awarded_certificates: {
    this_period: number,
    previous_period: number,
  },
  employees_assigned: {
    this_period: number,
    previous_period: number,
  },
  employees_started: {
    this_period: number,
    previous_period: number,
  },
  employees_finished: {
    this_period: number,
    previous_period: number,
  },
  employees_stopped: {
    this_period: number,
    previous_period: number,
  },
}

export type LearnGraphData = {
  [key: string]: number
};

export class TeamProgressStatistic {
  constructor(
    public teamProgress: TeamProgress,
  ) {
  }

  public static fromJson(json: any): TeamProgressStatistic {
    return new TeamProgressStatistic(
      TeamProgress.fromJson(json['team_progress']),
    );
  }
}

export class TeamProgress {
  constructor(
    public previousTeamProgress: number,
    public currentTeamProgress: number,
  ) {
  }

  public static fromJson(json: any): TeamProgress {
    return new TeamProgress(
      json['previous_team_progress'],
      json['current_team_progress'],
    );
  }
}

export class EmployeeProgress {
  constructor(
    public employeeId: number,
    public startDate: Date,
    public assignedModules: number,
    public finishedModules: number,
  ) {
  }

  public static fromJson(json: any): EmployeeProgress {
    return new EmployeeProgress(
      json['employee_id'],
      new Date(json['start_date']),
      json['assigned_modules'],
      json['finished_modules']
    )
  }
}

export type TeamProgressGraphStatistics = Record<string, Record<string, number>>;


export default class LearnRepository extends AbstractRepository {
  /**
   * List all teams in the system
   */
  public async learnStatistics(): Promise<LearnStatistics> {
    let response = await this.request<{ data: LearnStatistics }>('GET', 'learn/statistics')
    return response.data
  }
  public async listCategories(): Promise<Array<LearnCategory>> {
    let response = await this.request<{ data: { [id: number]: LearnCategory } }>('GET', 'learn/categories');
    return this.unpackDataCollectionResponse(response);
  }

  public async addCategory(name: string, parentCategoryId: string|null, isPinned: boolean, icon?: string): Promise<LearnCategory> {
    let r = await this.request<{ data: LearnCategory }>('POST', 'learn/categories', {
      name,
      is_pinned: isPinned,
      ...(parentCategoryId != null ? {parent_category_id: parentCategoryId} : {}),
      ...(icon ? { icon: icon } : {}),
    });
    return r.data;
  }

  public async categoryDashboard(categoryId: string): Promise<any> {
    return await this.request<{ data: object, generated_at: string }>('GET', `learn/categories/${categoryId}/dashboard`);
  }

  public async editCategory(categoryId: string, name: string, parentCategoryId: string|null, isPinned: boolean, icon?: string): Promise<LearnCategory> {
    let r = await this.request<{ data: LearnCategory }>('PUT', `learn/categories/${categoryId}`, {
      parent_category_id: parentCategoryId,
      name,
      is_pinned: isPinned,
      ...(icon ? { icon: icon } : {}),
    });
    return r.data;
  }

  public async moveCategory(categoryId: string, moveToCategoryId: string): Promise<LearnCategory> {
    let c = await this.request<{ data: LearnCategory }>('PUT', `learn/categories/${categoryId}/move`, {
      move_to_category_id: moveToCategoryId,
    });
    return c.data;
  }

  public async pinCategory(categoryId: string): Promise<LearnCategory> {
    let c = await this.request<{ data: LearnCategory }>('PUT', `learn/categories/${categoryId}/pin`, {})
    return c.data;
  }

  public async unpinCategory(categoryId: string): Promise<LearnCategory> {
    let c = await this.request<{ data: LearnCategory }>('PUT', `learn/categories/${categoryId}/unpin`, {})
    return c.data;
  }

  public async deleteCategory(categoryId: string): Promise<void> {
    await this.request<{ data: LearnCategory }>('DELETE', `learn/categories/${categoryId}`);
  }

  public async listModules(): Promise<Array<LearnModule>> {
    let response = await this.request<{ data: { [id: number]: LearnModule } }>('GET', 'learn/modules');
    return this.unpackDataCollectionResponse(response);
  }

  public async addModule(name: string, description: string, mandatory: boolean, type: string, categoryId: string|null): Promise<LearnModule> {
    let r = await this.request<{ data: LearnModule }>('POST', 'learn/modules', {
      name,
      description,
      mandatory,
      certificate_type: type,
      ...(categoryId != null ? {category_id: categoryId} : {})
    });
    return r.data;
  }
  public async editModule(leanCategoryId: string|null, moduleId: string, name: string, description: string, mandatory: boolean, chapters: string[], type: string): Promise<LearnModule> {
    let r = await this.request<{ data: LearnModule }>('POST', `learn/modules/${moduleId}`, {
      learn_category_id: leanCategoryId,
      name,
      description,
      mandatory,
      chapters,
      certificate_type: type
    });
    return r.data;
  }
  public async archiveModule(module: LearnModule): Promise<void> {
    await this.request<{ data: LearnModule }>('POST', `learn/modules/${module.id}`, {
      learn_category_id: module.category_id,
      archived: true,
    });
  }
  public async restoreModule(module: LearnModule): Promise<void> {
    await this.request<{ data: LearnModule }>('POST', `learn/modules/${module.id}`, {
      learn_category_id: module.category_id,
      archived: false,
    });
  }
  public async moveModuleToCategory(moduleId: string, categoryId: string|null): Promise<LearnModule> {
    let r = await this.request<{ data: LearnModule }>('POST', `learn/modules/${moduleId}`, {
      learn_category_id: categoryId,
    });
    return r.data;
  }
  public async readModule(moduleId: string): Promise<LearnModule> {
    let r = await this.request<{ data: LearnModule }>('GET', `learn/modules/${moduleId}`);
    return r.data;
  }
  public async readModuleWithProgress(moduleId: string): Promise<LearnModule> {
    let r = await this.request<{ data: LearnModule }>('GET', `learn/modules/${moduleId}/progress`);
    return r.data;
  }
  public async addComponent(moduleId: string, name: string, chapter: string|null): Promise<LearnComponent> {
    let r = await this.request<{ data: LearnComponent }>('POST', `learn/modules/${moduleId}/components`, {
      name,
      chapter,
    });
    return r.data;
  }
  public async listComponents(moduleId: string): Promise<Array<LearnComponent>> {
    let response = await this.request<{ data: { [id: number]: LearnComponent } }>('GET', `learn/modules/${moduleId}/components`);
    return this.unpackDataCollectionResponse(response);
  }
  public async readComponent(moduleId: string, componentId: string): Promise<LearnModule> {
    let r = await this.request<{ data: LearnModule }>('GET', `learn/modules/${moduleId}/components/${componentId}`);
    return r.data;
  }
  public async editComponent(moduleId: string, componentId: string, name: string, chapter: string|null): Promise<LearnComponent> {
    let r = await this.request<{ data: LearnComponent }>('POST', `learn/modules/${moduleId}/components/${componentId}`, {
      name,
      chapter,
    });
    return r.data;
  }

  public async editComponentsOrder(moduleId: string, components: LearnComponent[]): Promise<LearnComponent> {
    let r = await this.request<{ data: LearnComponent }>('POST', `learn/modules/${moduleId}/components/reorder`, {
      components: Object.fromEntries(components.map(component => [component.id, component.order])),
    });
    return r.data;
  }

  public async deleteComponent(moduleId: string, componentId: string): Promise<void> {
    await this.request<{}>('DELETE', `learn/modules/${moduleId}/components/${componentId}`);
  }

  public async addTextCard(moduleId: string, componentId: string, title: string, description: string): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards`, {
      type: 'text',
      title,
      description,
    });
    return r.data;
  }
  public async editTextCard(moduleId: string, componentId: string, cardId: string, title: string, description: string): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`, {
      type: 'text',
      title,
      description,
    });
    return r.data;
  }
  public async addQuestionCard(moduleId: string, componentId: string, title: string, description: string, options: string[], hasCorrectOption: boolean, correctOption: number|number[]|null, selectMultiple: boolean,  openFields?: number|number[]|null): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards`, {
      type: 'question',
      title,
      description,
      properties: {
        options,
        has_correct_option: hasCorrectOption,
        correct_option: correctOption,
        can_select_multiple: selectMultiple,
        open_fields: openFields,
      }
    });
    return r.data;
  }
  public async editQuestionCard(moduleId: string, componentId: string, cardId: string, title: string, description: string, options: string[], hasCorrectOption: boolean, correctOption: number|number[]|null, selectMultiple: boolean, openFields?: number|number[]|null): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`, {
      type: 'question',
      title,
      description,
      properties: {
        options,
        has_correct_option: hasCorrectOption,
        correct_option: correctOption,
        can_select_multiple: selectMultiple,
        open_fields: openFields,
      }
    });
    return r.data;
  }
  public async addMediaQuestionCard(moduleId: string, componentId: string, title: string, description: string, options: string[], hasCorrectOption: boolean, correctOption: number|number[]|null, selectMultiple: boolean): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards`, {
      type: 'media_question',
      title,
      description,
      properties: {
        options,
        has_correct_option: hasCorrectOption,
        correct_option: correctOption,
        can_select_multiple: selectMultiple,
      }
    });
    return r.data;
  }
  public async editMediaQuestionCard(moduleId: string, componentId: string, cardId: string, title: string, description: string, options: string[], hasCorrectOption: boolean, correctOption: number|number[]|null, selectMultiple: boolean): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`, {
      type: 'media_question',
      title,
      description,
      properties: {
        options,
        has_correct_option: hasCorrectOption,
        correct_option: correctOption,
        can_select_multiple: selectMultiple,
      }
    });
    return r.data;
  }
  public async addMediaToMediaQuestionCard(moduleId: string, componentId: string, cardId: string, title: string, description: string, file?: File): Promise<LearnCard> {
    const formData = new FormData()
    formData.append('type', 'media_question')
    formData.append('title', title)
    formData.append('description', description)
    if (file) {
      formData.append('media', file)
    }
    let r = await this.formPostRequest<{ data: LearnCard }>(`learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`, formData);
    return r.data;
  }
  public async addMediaCard(moduleId: string, componentId: string, title: string, description: string, file: File, options?: string[], hasCorrectOption?: boolean, correctOption?: number|number[]|null, openFields?: number|number[]|null): Promise<LearnCard> {
    const formData = new FormData()
    formData.append('type', 'media')
    formData.append('title', title)
    formData.append('description', description)
    formData.append('media', file)
    const properties = {
        options,
        has_correct_option: hasCorrectOption,
        correct_option: correctOption,
        open_fields: openFields,
      }
    formData.append('properties', JSON.stringify(properties));
    let r = await this.formPostRequest<{ data: LearnCard }>(`learn/modules/${moduleId}/components/${componentId}/cards`, formData);
    return r.data;
  }
  public async editMediaCard(moduleId: string, componentId: string, cardId: string, title: string, description: string, file?: File): Promise<LearnCard> {
    const formData = new FormData()
    formData.append('type', 'media')
    formData.append('title', title)
    formData.append('description', description)
    if (file) {
      formData.append('media', file)
    }
    let r = await this.formPostRequest<{ data: LearnCard }>(`learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`, formData);
    return r.data;
  }
  public async editMediaCardWithQuestions(moduleId: string, componentId: string, cardId: string, title: string, description: string, options?: string[], hasCorrectOption?: boolean, correctOption?: number|number[]|null, file?: File, openFields?: number|number[]|null): Promise<LearnCard> {
    const formData = new FormData()
    formData.append('type', 'media_question')
    formData.append('title', title)
    formData.append('description', description)
    if (file) {
      formData.append('media', file)
    }
    const properties = {
      options,
      has_correct_option: hasCorrectOption,
      correct_option: correctOption,
      open_fields: openFields,
    }
    formData.append('properties', JSON.stringify(properties));
    let r = await this.formPostRequest<{ data: LearnCard }>(`learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`, formData);
    return r.data;
  }
  public async addOpenCard(moduleId: string, componentId: string, title: string, description: string): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards`, {
      type: 'open',
      title,
      description,
    });
    return r.data;
  }
  public async editOpenCard(moduleId: string, componentId: string, cardId: string, title: string, description: string): Promise<LearnCard> {
    let r = await this.request<{ data: LearnCard }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`, {
      type: 'open',
      title,
      description,
    });
    return r.data;
  }
  public async deleteCard(moduleId: string, componentId: string, cardId: string): Promise<void> {
    await this.request<{}>('DELETE', `learn/modules/${moduleId}/components/${componentId}/cards/${cardId}`);
  }

  public async editCardsOrder(moduleId: string, componentId: string, cards: LearnCard[]): Promise<LearnComponent> {
    let r = await this.request<{ data: LearnComponent }>('POST', `learn/modules/${moduleId}/components/${componentId}/cards/reorder`, {
      cards: Object.fromEntries(cards.map(card => [card.id, card.order])),
    });
    return r.data;
  }

  public async assignModuleToTeam(moduleId: string, teamId: number): Promise<void> {
    await this.request<{}>('POST', `learn/modules/${moduleId}/assign`, {
      team: teamId,
    });
  }
  public async assignModuleToEmployee(moduleId: string, employeeId: number): Promise<void> {
    await this.request<{}>('POST', `learn/modules/${moduleId}/assign`, {
      employee: employeeId,
    });
  }
  public async assignModuleToEmployees(moduleId: string, employeeIds: number[]): Promise<void> {
    await this.request<{}>('POST', `learn/modules/${moduleId}/assign`, {
      employee_ids: employeeIds,
    });
  }
  public async unassignModuleToEmployees(moduleId: string, employeeIds: number[]): Promise<void> {
    await this.request<{}>('POST', `learn/modules/${moduleId}/unassign`, {
      employee_ids: employeeIds,
    });
  }
  public async sendModuleReminders(moduleId: string, employeeIds: number[]): Promise<void> {
    await this.request<{}>('POST', `learn/modules/${moduleId}/reminders`, {
      employee_ids: employeeIds,
    });
  }
  public async listMyCertificates(): Promise<Array<LearnCertificate>> {
    let response = await this.request<{ data: { [id: number]: LearnCertificate } }>('GET', 'learn/my_certificates');
    return this.unpackDataCollectionResponse(response);
  }
  public async listEmployeeCertificates(employeeId: number): Promise<Array<LearnCertificate>> {
    let response = await this.request<{ data: { [id: number]: LearnCertificate } }>('GET', `employee/${employeeId}/learn_certificates`);
    return this.unpackDataCollectionResponse(response);
  }
  public async listEmployeeAssignments(employeeId: number): Promise<Array<MyLearnAssignment>> {
    let response = await this.request<{ data: { [id: number]: MyLearnAssignment } }>('GET', `employee/${employeeId}/learn_assignments`);
    return this.unpackDataCollectionResponse(response);
  }

  public async exportModuleCsv(moduleId: string, employeeIds: number[]): Promise<Blob> {
    const response = await this.rawRequest('POST', `learn/modules/${moduleId}/export`, {
      format: 'csv',
      employee_ids: employeeIds,
    })
    return response.blob()
  }

  public async search(search: string)  {
    const response = await this.request<{ data: LearnSearchResult[] }>('POST', `learn/search`, {
      query: search
    });
    return this.unpackDataCollectionResponse(response);
  }

  public async fetchProgramDashboardStatistics(from: Date, upTo: Date, moduleIds: string[]): Promise<LearnDashboardStatistic> {
    const response = await this.request<{ data: LearnDashboardStatistic }>('POST', `learn/program-dashboard-stats`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds
    })
    return response.data
  }

  public async fetchStartedModulesForGraph(from: Date, upTo: Date, moduleIds: string[], timeUnit: string): Promise<LearnGraphData> {
    const response = await this.request<{ data: LearnGraphData }>('POST', `learn/program-dashboard-stats/graphs/started`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      unit: timeUnit,
    })

    return response.data
  }

  public async fetchOpenModulesForGraph(from: Date, upTo: Date, moduleIds: string[], timeUnit: string): Promise<LearnGraphData> {
    const response = await this.request<{ data: LearnGraphData }>('POST', `learn/program-dashboard-stats/graphs/open`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      unit: timeUnit,
    })

    return response.data
  }

  public async fetchContinuedModulesForGraph(from: Date, upTo: Date, moduleIds: string[], timeUnit: string): Promise<LearnGraphData> {
    const response = await this.request<{ data: LearnGraphData }>('POST', `learn/program-dashboard-stats/graphs/continued`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      unit: timeUnit,
    })

    return response.data
  }

  public async fetchProgressForGraph(from: Date, upTo: Date, moduleIds: string[], timeUnit: string, teamIds?: number[]): Promise<LearnGraphData> {
    const response = await this.request<{ data: LearnGraphData }>('POST', `learn/program-dashboard-stats/graphs/progress`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      unit: timeUnit,
      ...(teamIds ? {team_ids: teamIds} : {})
    })

    return response.data
  }

  public async fetchTeamProgressDashboardStatistics(from: Date, upTo: Date, moduleIds: string[], teamIds: number[]): Promise<LearnDashboardStatistic> {
    const response = await this.request<{ data: LearnDashboardStatistic }>('POST', `learn/team-progress-dashboard-stats`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      team_ids: teamIds,
    })
    return response.data
  }

  public async fetchTeamProgress(from: Date, upTo: Date, moduleIds: string[], teamIds: number[]): Promise<TeamProgressStatistic> {
    const response = await this.request<{ data: object }>('POST', `learn/team-progress-team-stats`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      team_ids: teamIds,
    })
    return TeamProgressStatistic.fromJson(response.data)
  }

  public async fetchEmployeesProgress(from: Date, upTo: Date, moduleIds: string[], teamIds: number[]): Promise<EmployeeProgress[]> {
    const response = await this.request<{ data: object[] }>('POST', `learn/team-progress-employees-stats`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      team_ids: teamIds,
    })
    return response.data.map((d) => EmployeeProgress.fromJson(d))
  }

  public async fetchTeamProgressForGraph(from: Date, upTo: Date, moduleIds: string[], timeUnit: string, teamIds?: number[]): Promise<LearnGraphData> {
    const response = await this.request<{ data: LearnGraphData }>('POST', `learn/team-progress-graph-stats`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      module_ids: moduleIds,
      unit: timeUnit,
      team_ids: teamIds
    })

    return response.data
  }

  public async fetchTeamProgramProgressForGraph(from: Date, upTo: Date, category: string, timeUnit: string, teamIds?: number[]): Promise<TeamProgressGraphStatistics> {
    const response = await this.request<{ data: TeamProgressGraphStatistics }>('POST', `learn/team-program-progress-graph-stats`, {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(upTo).add(1, 'd').format('YYYY-MM-DD'),
      category: category,
      unit: timeUnit,
      team_ids: teamIds
    })

    return response.data
  }
}
